import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import {
  X,
  PanelRightClose,
  PanelRightOpen,
  MessageSquare,
  MonitorSmartphone,
} from "lucide-react";
import IconButton from "@/components/common/GoogleIconButton";
import { useReducedMotion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useUIStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";
import { useBusinessUnitStore } from "@/stores/businessUnit";
import { useIsMobile } from "@/hooks/useBreakpoint";
import { useShallow } from "zustand/react/shallow";
import { useChatStore } from "../stores/chat";
import { completeReviewedMessages } from "../utils/reviewCompletion";
import { useStreaming } from "../hooks/useStreaming";
import { useChats, useChatMessages } from "../hooks/useChatData";
import { useEffectiveFacility } from "../hooks/useEffectiveFacility";
import { useReviewDialogs } from "../hooks/useReviewDialogs";
import { deleteChat, generateChatTitle } from "@/api/chat";
import { ChatHistoryList } from "./ChatHistoryList";
import { MessageBubble } from "../chat/MessageBubble";
import { ChatInput } from "../chat/ChatInput";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  contextLabel,
  contextualElementsLabel,
  keyMetricsLabel,
} from "../lib/contextLabels";
import { getSuggestedQuestions } from "../lib/suggestedQuestions";
import { useLocation } from "react-router-dom";
import {
  capturePageContext,
  useCurrentPageContext,
} from "@/components/ai/useCurrentPageContext";
import {
  resolveContextInclusion,
  shouldAskForScreenContext,
} from "../lib/contextConsent";
import type { FileAttachment, MessageSummary, ScreenContext } from "@/types";

const EmissionReviewModal = lazy(
  () => import("@/components/emissions/EmissionReviewModal"),
);
const EmissionSummaryModal = lazy(() =>
  import("@/components/emissions/EmissionSummaryModal").then((m) => ({
    default: m.EmissionSummaryModal,
  })),
);

/** Indicates context that can be attached to the next message. */
function ScreenContextPill({ context }: { context: ScreenContext }) {
  const metrics = keyMetricsLabel(context.keyMetrics);
  const label = contextLabel({ page: "on screen", ...context });
  const details = contextualElementsLabel(context);
  const isActiveViewList =
    context.activeView === "list" ||
    context.activeView === "dashboard" ||
    context.elements.some(
      (el) => el.includes("-list") || el.includes("-dashboard"),
    );
  const isMetricsLoading =
    isActiveViewList &&
    (!context.keyMetrics || Object.keys(context.keyMetrics).length === 0) &&
    !context.pageTitle &&
    !context.visibleRecords?.length;
  return (
    <div
      role="status"
      aria-label={`Available screen context: ${label}; ${details}${
        metrics ? ` — ${metrics}` : ""
      }`}
      className="flex flex-wrap items-center gap-2 border-b border-border bg-primary-5 px-4 sm:px-6 py-2 shrink-0 min-w-0"
    >
      <MonitorSmartphone className="h-3.5 w-3.5 shrink-0 text-primary" />
      <span
        className="min-w-0 flex-1 truncate text-xs text-foreground/80"
        title={label}
      >
        {label}
      </span>
      {details !== "current view" && (
        <span
          className="hidden sm:inline min-w-0 max-w-[40%] truncate text-[11px] text-muted-foreground"
          title={details}
        >
          {details}
        </span>
      )}
      {isMetricsLoading && (
        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground animate-pulse">
          Loading metrics…
        </span>
      )}
      {!isMetricsLoading && metrics && (
        <span
          className="w-full min-w-0 line-clamp-2 break-words text-[11px] text-muted-foreground"
          title={metrics}
          aria-label={`Live metrics: ${metrics}`}
        >
          {metrics}
        </span>
      )}
    </div>
  );
}

export const AIPanel = memo(function AIPanel({
  contentWidth = 0,
}: {
  contentWidth?: number;
}) {
  const aiPanelOpen = useUIStore((s) => s.aiPanelOpen);
  const setAIPanelOpen = useUIStore((s) => s.setAIPanelOpen);
  const isMobile = useIsMobile();
  const reducedMotion = useReducedMotion();

  // contentWidth includes both the page and panel, even when the panel is
  // closed or overlaid. Reserve its full width before deciding to dock.
  const PANEL_WIDTH = 420;
  const dockedPanelWidth = Math.min(
    PANEL_WIDTH,
    Math.max(320, Math.round(contentWidth * 0.35)),
  );
  const MIN_DOCKED_CONTENT_WIDTH = 560;
  const overlay =
    isMobile || contentWidth < MIN_DOCKED_CONTENT_WIDTH + dockedPanelWidth;
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user?.userId);
  const queryClient = useQueryClient();
  const businessUnits = useBusinessUnitStore((s) => s.businessUnits);
  const { facility } = useEffectiveFacility();

  const {
    chatId,
    messages,
    isThinking,
    isStreaming,
    streamingContent,
    currentProgress,
    followUpQuestions,
    logEmissionEnabled,
    sidebarOpen,
    pageContext,
    setChatId,
    addMessage,
    updateLastUserMessage,
    setFollowUpQuestions,
    setSidebarOpen,
    resetChat,
    formContext,
  } = useChatStore(
    useShallow((s) => ({
      chatId: s.chatId,
      messages: s.messages,
      isThinking: s.isThinking,
      isStreaming: s.isStreaming,
      streamingContent: s.streamingContent,
      currentProgress: s.currentProgress,
      followUpQuestions: s.followUpQuestions,
      logEmissionEnabled: s.logEmissionEnabled,
      sidebarOpen: s.sidebarOpen,
      pageContext: s.pageContext,
      formContext: s.formContext,
      setChatId: s.setChatId,
      addMessage: s.addMessage,
      updateLastUserMessage: s.updateLastUserMessage,
      setFollowUpQuestions: s.setFollowUpQuestions,
      setSidebarOpen: s.setSidebarOpen,
      resetChat: s.resetChat,
    })),
  );
  const { pathname } = useLocation();
  const currentPageContext = useCurrentPageContext(pageContext);
  const getCurrentPageContext = useCallback(
    () => capturePageContext(pathname, useChatStore.getState().pageContext),
    [pathname],
  );
  const [screenContextPrompt, setScreenContextPrompt] = useState<{
    message: string;
  } | null>(null);

  // Empty-state chips follow the page the user is on instead of offering the
  // same account-wide questions everywhere.
  const suggestedQuestions = useMemo(
    () =>
      getSuggestedQuestions({
        formContext,
        screenContext: currentPageContext.screenContext,
      }),
    [formContext, currentPageContext.screenContext],
  );

  const { sendMessage, abort, detach } = useStreaming();
  const {
    chats,
    hasMore,
    loading: chatsLoading,
    loadingMore: chatsLoadingMore,
    error: chatsError,
    loadMore,
    refreshChats,
  } = useChats();
  const { loadChatMessages } = useChatMessages();

  const { reviewDialog, setReviewDialog, summaryDialog, setSummaryDialog } =
    useReviewDialogs("panel");

  const handleClose = useCallback(() => {
    setAIPanelOpen(false);
  }, [setAIPanelOpen]);

  useEffect(() => {
    if (!aiPanelOpen || !overlay) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    requestAnimationFrame(() => closeButtonRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocused?.focus();
    };
  }, [aiPanelOpen, overlay, handleClose]);

  const handleScreenContextPromptHandled = useCallback(() => {
    setScreenContextPrompt(null);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen(!sidebarOpen);
  }, [sidebarOpen, setSidebarOpen]);

  const handleNewChat = useCallback(() => {
    detach();
    resetChat();
  }, [detach, resetChat]);

  const handleSelectChat = useCallback(
    async (chat: { id: number; title: string; created: string }) => {
      if (!token) return;
      detach();
      useChatStore.getState().setMessages([]);
      useChatStore.getState().setLogEmissionEnabled(false);
      setChatId(String(chat.id));
      setSidebarOpen(false);
      const fqs = await loadChatMessages(String(chat.id));
      if (useChatStore.getState().chatId !== String(chat.id)) return;
      if (fqs && fqs.length > 0) {
        setFollowUpQuestions(fqs);
      } else {
        setFollowUpQuestions(null);
      }
    },
    [
      token,
      detach,
      setChatId,
      setSidebarOpen,
      loadChatMessages,
      setFollowUpQuestions,
    ],
  );

  const handleDeleteChat = useCallback(
    async (chatIdToDelete: string) => {
      if (!token) return;
      try {
        await deleteChat(chatIdToDelete, token);
        if (useChatStore.getState().chatId === chatIdToDelete) {
          detach();
          resetChat();
        }
        await refreshChats();
      } catch {
        toast.error("Unable to delete this conversation");
      }
    },
    [token, detach, resetChat, refreshChats],
  );

  const handleSend = useCallback(
    (message: string, files?: FileAttachment[]) => {
      if (!token || useChatStore.getState().isStreaming) return false;

      const state = useChatStore.getState();
      const pageCtx = getCurrentPageContext();
      if (shouldAskForScreenContext(message, pageCtx.screenContext, state)) {
        setScreenContextPrompt({
          message,
        });
        return false;
      }

      const userMessage = {
        role: "user" as const,
        content: message,
        fileAttachments: files,
      };
      addMessage(userMessage);
      setFollowUpQuestions(null);

      const { includeScreenContext: includeCtx, includeFormContext } =
        resolveContextInclusion(state, state.formContext != null);
      const liveFormContext = state.formContext;

      let effectivePageContext: typeof state.pageContext = null;
      if (includeCtx && pageCtx?.screenContext) {
        effectivePageContext = pageCtx;
      } else if (pageCtx) {
        effectivePageContext = {
          page: pageCtx.page,
        };
      }

      useChatStore.getState().setIncludeScreenContext(null);
      useChatStore.getState().setIncludeFormContext(null);

      const pageContextSent = effectivePageContext;
      const screenContextSent = pageContextSent?.screenContext;
      if (pageContextSent && screenContextSent) {
        updateLastUserMessage((msg) => ({
          ...msg,
          screenContextSent: {
            page: pageContextSent.page,
            ...screenContextSent,
          },
        }));
      }

      void sendMessage(
        message,
        token,
        {
          chatId: chatId ?? undefined,
          businessUnitId: facility?.id,
          businessUnitName: facility?.name,
          logEmissionEnabled: logEmissionEnabled || undefined,
          files,
          pageContext: effectivePageContext ?? undefined,
          formContext: includeFormContext
            ? (liveFormContext ?? undefined)
            : undefined,
        },
        {
          onResult(id) {
            if (!chatId || id !== chatId) {
              setChatId(id);
              void refreshChats();
              void generateChatTitle(id, message, token).then(() =>
                refreshChats(),
              );
            }
          },
          onPendingReview(id, messageId) {
            setReviewDialog({ kind: "conversation", chatId: id, messageId });
          },
        },
      );
    },
    [
      token,
      chatId,
      facility,
      logEmissionEnabled,
      getCurrentPageContext,
      addMessage,
      updateLastUserMessage,
      setFollowUpQuestions,
      sendMessage,
      setChatId,
      refreshChats,
      setReviewDialog,
    ],
  );

  const handleSendFollowup = useCallback(
    (question: string) => {
      setFollowUpQuestions(null);
      void handleSend(question);
    },
    [handleSend, setFollowUpQuestions],
  );

  const handleReviewComplete = useCallback(
    (message: string, summary?: MessageSummary) => {
      if (!reviewDialog) return;
      void queryClient.invalidateQueries({ queryKey: ["emissions"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({
        queryKey: ["chat-messages", userId, reviewDialog.chatId],
      });
      setReviewDialog(null);
      useChatStore.setState((s) => {
        if (s.chatId !== reviewDialog.chatId) return s;
        const updated = completeReviewedMessages(
          s.messages,
          reviewDialog,
          summary,
        );
        const newMsg = {
          role: "assistant" as const,
          content: message,
          summary,
        };
        return { messages: [...updated, newMsg] };
      });
    },
    [reviewDialog, queryClient, userId, setReviewDialog],
  );

  const showChat = messages.length > 0 || isThinking;

  return (
    <div
      ref={panelRef}
      role={overlay ? "dialog" : "complementary"}
      aria-modal={overlay && aiPanelOpen ? "true" : undefined}
      aria-label="Green Mirror"
      aria-hidden={!aiPanelOpen}
      inert={!aiPanelOpen}
      style={
        overlay
          ? undefined
          : {
              width: aiPanelOpen ? dockedPanelWidth : 0,
              // Reserve the final space immediately on open. On close, keep
              // it until the content exits; never interpolate page width.
              transitionProperty: "width",
              transitionDuration: "0s",
              transitionDelay: aiPanelOpen || reducedMotion ? "0s" : "200ms",
            }
      }
      className={cn(
        "h-full overflow-hidden min-w-0",
        isMobile
          ? cn(
              "fixed inset-0 z-50 bg-card shadow-overlay transition-transform duration-200 ease-out",
              aiPanelOpen
                ? "translate-x-0"
                : "translate-x-full pointer-events-none",
            )
          : overlay
            ? cn(
                "fixed right-0 top-0 z-40 w-[420px] max-w-[100vw] bg-card shadow-overlay transition-transform duration-200 ease-out",
                aiPanelOpen
                  ? "translate-x-0"
                  : "translate-x-full pointer-events-none",
              )
            : cn(
                "shrink-0 bg-card shadow-overlay",
                aiPanelOpen && "border-l border-border",
              ),
      )}
    >
      <div
        style={overlay ? undefined : { width: dockedPanelWidth }}
        className={cn(
          "flex h-full flex-col",
          isMobile ? "w-screen" : overlay ? "w-[420px]" : "w-full",
          !overlay &&
            "transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none motion-reduce:transform-none",
          !overlay &&
            (aiPanelOpen
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-4"),
        )}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-background px-4 sm:px-6 py-3 sm:py-4 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Always rendered: dropping this control in the empty state left
                users with no way to reach their chat history. */}
            <IconButton
              ref={closeButtonRef}
              size="small"
              onClick={handleToggleSidebar}
              className="shrink-0"
              aria-label={
                sidebarOpen ? "Hide chat history" : "Show chat history"
              }
              aria-expanded={sidebarOpen}
              sx={{
                color: "var(--muted-foreground)",
                "&:hover": {
                  color: "var(--foreground)",
                },
              }}
            >
              {sidebarOpen ? (
                <PanelRightClose className="h-4 w-4" />
              ) : (
                <PanelRightOpen className="h-4 w-4" />
              )}
            </IconButton>
            {/* The title no longer flips between the generic "Chat" and the
                product name depending on conversation state. */}
            <span className="text-sm font-semibold tracking-tight truncate">
              Green Mirror
            </span>
          </div>
          <div className="flex items-center gap-1">
            {showChat && !sidebarOpen && (
              <IconButton
                size="small"
                onClick={handleNewChat}
                aria-label="New chat"
                sx={{
                  color: "var(--muted-foreground)",
                  "&:hover": {
                    color: "var(--foreground)",
                  },
                }}
              >
                <MessageSquare className="h-4 w-4" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label="Close AI panel"
              sx={{
                color: "var(--muted-foreground)",
                borderRadius: "0.5rem",
                "&:hover": {
                  color: "var(--foreground)",
                },
              }}
            >
              <X className="h-4 w-4" />
            </IconButton>
          </div>
        </div>

        {sidebarOpen ? (
          <ChatHistoryList
            chats={chats}
            activeChatId={chatId}
            onNewChat={handleNewChat}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
            hasMore={hasMore}
            loading={chatsLoading}
            loadingMore={chatsLoadingMore}
            error={chatsError}
            onLoadMore={loadMore}
            onRetry={refreshChats}
          />
        ) : (
          <>
            {currentPageContext.screenContext && (
              <ScreenContextPill context={currentPageContext.screenContext} />
            )}
            <MessageBubble
              messages={messages}
              loading={false}
              currentProgress={currentProgress}
              isThinking={isThinking}
              streamingContent={streamingContent}
              suggestedQuestions={suggestedQuestions}
              onSampleClick={(q) => void handleSend(q)}
              onReviewClick={(batchId, recordCount) =>
                chatId &&
                setReviewDialog({
                  kind: "intake",
                  chatId,
                  batchId,
                  recordCount,
                })
              }
              onEmissionReviewClick={(id, messageId) =>
                setReviewDialog({ kind: "conversation", chatId: id, messageId })
              }
              onSummaryClick={(summary) => setSummaryDialog(summary)}
              compact
            />
            <ChatInput
              onSend={handleSend}
              disabled={isStreaming}
              showPlaceholderHint={messages.length === 0}
              followUpQuestions={followUpQuestions}
              onSendFollowup={handleSendFollowup}
              onStop={abort}
              pageContextOverride={currentPageContext}
              getPageContext={getCurrentPageContext}
              screenContextPrompt={screenContextPrompt}
              onScreenContextPromptHandled={handleScreenContextPromptHandled}
              compact
            />
          </>
        )}
      </div>

      <Suspense fallback={null}>
        {aiPanelOpen && reviewDialog?.kind === "intake" && (
          <EmissionReviewModal
            chatId={reviewDialog.chatId}
            batchId={reviewDialog.batchId}
            recordCount={reviewDialog.recordCount}
            businessUnits={businessUnits}
            onClose={() => setReviewDialog(null)}
            onComplete={handleReviewComplete}
          />
        )}
        {aiPanelOpen && reviewDialog?.kind === "conversation" && (
          <EmissionReviewModal
            chatId={reviewDialog.chatId}
            messageId={reviewDialog.messageId}
            businessUnits={businessUnits}
            onClose={() => setReviewDialog(null)}
            onComplete={handleReviewComplete}
          />
        )}
        {aiPanelOpen && summaryDialog && (
          <EmissionSummaryModal
            summary={summaryDialog}
            businessUnits={businessUnits}
            onClose={() => setSummaryDialog(null)}
          />
        )}
      </Suspense>
    </div>
  );
});
